import { getCurrentTenantCode } from '@/commons';
import { useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_PREFIX = 'car-export-mobile:route-scroll-position';
const STORAGE_VERSION = 1;
const RESTORE_TIMEOUT_MS = 10_000;

type ScrollPosition = {
  left: number;
  top: number;
};

type RouteScrollState = {
  containers: Record<string, ScrollPosition>;
  window: ScrollPosition;
};

type StoredRouteScrollState = RouteScrollState & {
  version: typeof STORAGE_VERSION;
};

function toStorageSegment(value: string) {
  return encodeURIComponent(value || '-');
}

function getRouteScrollStorageKey(input: {
  key: string;
  pathname: string;
  search: string;
  tenantCode?: string;
}) {
  const tenantCode = input.tenantCode ?? getCurrentTenantCode();
  return [
    STORAGE_PREFIX,
    toStorageSegment(tenantCode),
    toStorageSegment(input.key),
    toStorageSegment(`${input.pathname}${input.search}`),
  ].join(':');
}

function isValidCoordinate(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isValidPosition(value: unknown): value is ScrollPosition {
  if (!value || typeof value !== 'object') return false;
  return (
    isValidCoordinate(Reflect.get(value, 'left')) &&
    isValidCoordinate(Reflect.get(value, 'top'))
  );
}

function readPosition(storageKey: string): RouteScrollState | undefined {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return undefined;

    const value = JSON.parse(raw) as Partial<StoredRouteScrollState>;
    if (
      value.version !== STORAGE_VERSION ||
      !isValidPosition(value.window) ||
      !value.containers ||
      typeof value.containers !== 'object' ||
      Object.values(value.containers).some(
        (position) => !isValidPosition(position),
      )
    ) {
      sessionStorage.removeItem(storageKey);
      return undefined;
    }

    return {
      containers: value.containers as Record<string, ScrollPosition>,
      window: value.window,
    };
  } catch {
    return undefined;
  }
}

function getScrollContainers() {
  return Array.from(
    document.querySelectorAll<HTMLElement>('[data-scroll-container]'),
  );
}

function captureScrollState(): RouteScrollState {
  const containers: Record<string, ScrollPosition> = {};
  for (const element of getScrollContainers()) {
    const name = element.dataset.scrollContainer;
    if (!name) continue;
    containers[name] = {
      left: Math.max(0, element.scrollLeft),
      top: Math.max(0, element.scrollTop),
    };
  }

  return {
    containers,
    window: {
      left: Math.max(0, window.scrollX),
      top: Math.max(0, window.scrollY),
    },
  };
}

function writePosition(storageKey: string, state: RouteScrollState) {
  try {
    const value: StoredRouteScrollState = {
      ...state,
      version: STORAGE_VERSION,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Storage can be unavailable in privacy-restricted browsers.
  }
}

function getMaximumScrollTop() {
  const documentHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body.scrollHeight,
  );
  return Math.max(0, documentHeight - window.innerHeight);
}

function getEmptyScrollState(): RouteScrollState {
  const containers: Record<string, ScrollPosition> = {};
  for (const element of getScrollContainers()) {
    const name = element.dataset.scrollContainer;
    if (name) containers[name] = { left: 0, top: 0 };
  }
  return { containers, window: { left: 0, top: 0 } };
}

function restorePosition(state: RouteScrollState, onSettled: () => void) {
  let stopped = false;
  let observer: ResizeObserver | undefined;
  const timers: { retry?: number; timeout?: number } = {};

  const stop = (notifySettled: boolean) => {
    if (stopped) return;
    stopped = true;
    observer?.disconnect();
    if (timers.retry !== undefined) window.clearInterval(timers.retry);
    if (timers.timeout !== undefined) window.clearTimeout(timers.timeout);
    for (const eventName of ['keydown', 'pointerdown', 'touchstart', 'wheel']) {
      window.removeEventListener(eventName, settle, true);
    }
    if (notifySettled) onSettled();
  };

  const settle = () => stop(true);

  const attempt = () => {
    if (stopped) return;
    window.scrollTo({
      behavior: 'auto',
      left: state.window.left,
      top: state.window.top,
    });

    let containersReady = true;
    for (const [name, position] of Object.entries(state.containers)) {
      const element = getScrollContainers().find(
        (candidate) => candidate.dataset.scrollContainer === name,
      );
      if (!element) {
        containersReady = false;
        continue;
      }
      element.scrollLeft = position.left;
      element.scrollTop = position.top;
      if (
        position.top >
        Math.max(0, element.scrollHeight - element.clientHeight) + 1
      ) {
        containersReady = false;
      }
    }

    if (state.window.top <= getMaximumScrollTop() + 1 && containersReady) {
      settle();
    }
  };

  attempt();
  if (stopped) return () => undefined;

  if (typeof ResizeObserver === 'function') {
    observer = new ResizeObserver(attempt);
    observer.observe(document.documentElement);
    observer.observe(document.body);
    for (const element of getScrollContainers()) observer.observe(element);
  }
  timers.retry = window.setInterval(attempt, 100);

  timers.timeout = window.setTimeout(settle, RESTORE_TIMEOUT_MS);
  for (const eventName of ['keydown', 'pointerdown', 'touchstart', 'wheel']) {
    window.addEventListener(eventName, settle, true);
  }

  return () => stop(false);
}

/**
 * Keeps scroll state with the browser history entry instead of the route name.
 * PUSH opens at the top, POP restores, and REPLACE preserves the current view.
 */
export function RouteScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (!('scrollRestoration' in window.history)) return;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    const storageKey = getRouteScrollStorageKey({
      key: location.key,
      pathname: location.pathname,
      search: location.search,
    });
    const restoreState =
      navigationType === 'POP'
        ? (readPosition(storageKey) ?? getEmptyScrollState())
        : getEmptyScrollState();
    let writeFrame: number | undefined;
    let restoring = navigationType !== 'REPLACE';
    let latestState =
      navigationType === 'REPLACE' ? captureScrollState() : restoreState;

    const save = () => writePosition(storageKey, latestState);
    const captureLatest = () => {
      if (!restoring) latestState = captureScrollState();
    };
    const scheduleSave = () => {
      if (restoring) return;
      // Capture while the current route still owns the DOM. An effect cleanup
      // runs after the next route has committed and can otherwise persist a
      // scroll position already clamped by the incoming page's shorter layout.
      latestState = captureScrollState();
      if (writeFrame !== undefined) return;
      writeFrame = window.requestAnimationFrame(() => {
        writeFrame = undefined;
        save();
      });
    };

    const handlePageHide = () => {
      if (!restoring) latestState = captureScrollState();
      save();
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('keydown', captureLatest, true);
    window.addEventListener('pointerdown', captureLatest, true);
    document.addEventListener('scroll', scheduleSave, {
      capture: true,
      passive: true,
    });

    const stopRestore =
      navigationType === 'REPLACE'
        ? undefined
        : restorePosition(restoreState, () => {
            restoring = false;
            latestState = captureScrollState();
            save();
          });

    return () => {
      stopRestore?.();
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('keydown', captureLatest, true);
      window.removeEventListener('pointerdown', captureLatest, true);
      document.removeEventListener('scroll', scheduleSave, true);
      if (writeFrame !== undefined) window.cancelAnimationFrame(writeFrame);
      save();
    };
  }, [location.key, location.pathname, location.search, navigationType]);

  return null;
}
